<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import APIHelper from 'dashboard/helper/APIHelper';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import { Multiselect } from 'vue-multiselect';

const props = defineProps({
  sourceAgent: { type: Object, required: true },
  agents: { type: Array, required: true },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const authenticatedAxios = APIHelper(window.axios);
const currentAccountId = useMapGetter('getCurrentAccountId');

const mode = ref('single');
const includeAdmins = ref(false);
const includeResolved = ref(false);
const singleTarget = ref(null);
const multiTargets = ref([]);
const preview = ref(null);
const isLoading = ref(false);
const isApplying = ref(false);

const apiBase = computed(
  () => `/api/v1/accounts/${currentAccountId.value}/agents/${props.sourceAgent.id}/reassign_conversations`
);

const availableTargets = computed(() => {
  return props.agents.filter(a => {
    if (a.id === props.sourceAgent.id) return false;
    if (!includeAdmins.value && a.role === 'administrator') return false;
    return true;
  });
});

const targetIds = computed(() => {
  if (mode.value === 'single') {
    return singleTarget.value ? [singleTarget.value.id] : [];
  }
  return multiTargets.value.map(a => a.id);
});

const canPreview = computed(() => targetIds.value.length > 0);
const canApply = computed(() => preview.value && preview.value.total > 0 && !isApplying.value);

const fetchPreview = async () => {
  if (!canPreview.value) return;
  try {
    isLoading.value = true;
    const response = await authenticatedAxios.post(apiBase.value, {
      target_agent_ids: targetIds.value,
      include_resolved: includeResolved.value,
      preview: true,
    });
    preview.value = response.data;
  } catch (error) {
    useAlert(t('AGENT_MGMT.REASSIGN.ERROR'));
    preview.value = null;
  } finally {
    isLoading.value = false;
  }
};

const applyReassignment = async () => {
  if (!canApply.value) return;
  try {
    isApplying.value = true;
    const response = await authenticatedAxios.post(apiBase.value, {
      target_agent_ids: targetIds.value,
      include_resolved: includeResolved.value,
      preview: false,
    });
    useAlert(
      t('AGENT_MGMT.REASSIGN.SUCCESS', { count: response.data.total_reassigned })
    );
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.REASSIGN.ERROR'));
  } finally {
    isApplying.value = false;
  }
};

const resetPreview = () => {
  preview.value = null;
};

onMounted(() => {
  resetPreview();
});
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto p-6">
    <div class="mb-6">
      <h2 class="text-xl font-semibold text-n-slate-12">
        {{ t('AGENT_MGMT.REASSIGN.TITLE', { name: sourceAgent.name }) }}
      </h2>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ t('AGENT_MGMT.REASSIGN.SUBTITLE') }}
      </p>
    </div>

    <div class="flex flex-col gap-5">
      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ t('AGENT_MGMT.REASSIGN.MODE_LABEL') }}
        </label>
        <div class="flex gap-2">
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="mode"
              type="radio"
              value="single"
              @change="resetPreview"
            />
            <span>{{ t('AGENT_MGMT.REASSIGN.MODE_SINGLE') }}</span>
          </label>
          <label class="flex items-center gap-2 cursor-pointer ml-4">
            <input
              v-model="mode"
              type="radio"
              value="equal"
              @change="resetPreview"
            />
            <span>{{ t('AGENT_MGMT.REASSIGN.MODE_EQUAL') }}</span>
          </label>
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{
            mode === 'single'
              ? t('AGENT_MGMT.REASSIGN.TARGET_SINGLE')
              : t('AGENT_MGMT.REASSIGN.TARGET_MULTI')
          }}
        </label>
        <Multiselect
          v-if="mode === 'single'"
          v-model="singleTarget"
          :options="availableTargets"
          :placeholder="t('AGENT_MGMT.REASSIGN.TARGET_PLACEHOLDER')"
          label="name"
          track-by="id"
          @update:modelValue="resetPreview"
        />
        <Multiselect
          v-else
          v-model="multiTargets"
          :options="availableTargets"
          multiple
          :placeholder="t('AGENT_MGMT.REASSIGN.TARGET_PLACEHOLDER')"
          label="name"
          track-by="id"
          @update:modelValue="resetPreview"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label class="flex items-center gap-2 cursor-pointer text-sm">
          <input
            v-model="includeAdmins"
            type="checkbox"
            @change="resetPreview"
          />
          <span>{{ t('AGENT_MGMT.REASSIGN.INCLUDE_ADMINS') }}</span>
        </label>
        <label class="flex items-center gap-2 cursor-pointer text-sm">
          <input
            v-model="includeResolved"
            type="checkbox"
            @change="resetPreview"
          />
          <span>{{ t('AGENT_MGMT.REASSIGN.INCLUDE_RESOLVED') }}</span>
        </label>
      </div>

      <div v-if="preview" class="bg-n-blue-2 p-4 rounded-lg">
        <p class="text-sm font-medium text-n-blue-11 mb-2">
          {{ t('AGENT_MGMT.REASSIGN.PREVIEW_HEADER', { count: preview.total }) }}
        </p>
        <ul v-if="preview.distribution.length" class="text-sm text-n-slate-11 list-disc list-inside">
          <li v-for="row in preview.distribution" :key="row.agent_id">
            {{ row.agent_name }}: <strong>{{ row.count }}</strong>
          </li>
        </ul>
      </div>
    </div>

    <div class="flex justify-end gap-2 mt-8 pt-6 border-t border-n-container">
      <Button
        type="button"
        slate
        :label="t('AGENT_MGMT.REASSIGN.CANCEL')"
        @click="emit('close')"
      />
      <Button
        type="button"
        slate
        :label="t('AGENT_MGMT.REASSIGN.PREVIEW')"
        :is-loading="isLoading"
        :disabled="!canPreview"
        @click="fetchPreview"
      />
      <Button
        type="button"
        blue
        :label="t('AGENT_MGMT.REASSIGN.APPLY')"
        :is-loading="isApplying"
        :disabled="!canApply"
        @click="applyReassignment"
      />
    </div>
  </div>
</template>
